import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import BaseLayout from 'src/layouts/BaseLayout';

import SuspenseLoader from 'src/components/SuspenseLoader';
import ProtectedRoute from 'src/components/ProtectedRoute';
import AuthLandingRedirect from 'src/components/AuthLandingRedirect';

const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Authentication
const Login = Loader(lazy(() => import('src/pages/Login')));
const ProtectedCrypto = Loader(lazy(() => import('src/components/ProtectedCrypto')));

// Parts Management
const PartsAssemblies = Loader(lazy(() => import('src/content/applications/Parts/Assemblies/Assemblies')));
const PartsComponents = Loader(lazy(() => import('src/content/applications/Parts/Components/Components')));
const PartsSales = Loader(lazy(() => import('src/content/applications/Parts/Sales/Sales')));
const PartsCatalog = Loader(lazy(() => import('src/content/applications/Parts/Catalog/Catalog')));

// Budget Management
const BudgetDashboard = Loader(lazy(() => import('src/content/dashboards/Budget/BudgetDashboard')));
const BudgetDashboard2 = Loader(lazy(() => import('src/content/dashboards/Budget/BudgetDashboard2')));
const EnhancedBudgetDashboard = Loader(lazy(() => import('src/content/dashboards/Budget/EnhancedBudgetDashboard')));
const BudgetDebt = Loader(lazy(() => import('src/content/applications/Budget/Debt/Debt')));
const BudgetCatalog = Loader(lazy(() => import('src/content/applications/Budget/Catalog/Catalog')));
const BudgetSettings = Loader(lazy(() => import('src/content/applications/Budget/Settings/Settings')));
const BudgetReports = Loader(lazy(() => import('src/content/applications/Budget/Reports/Reports')));
const BudgetPayments = Loader(lazy(() => import('src/content/applications/Budget/Payments/Payments')));
const BudgetTransactions = Loader(lazy(() => import('src/content/applications/Budget/Transactions/Transactions')));
const BudgetAccounts = Loader(lazy(() => import('src/content/applications/Budget/Accounts/Accounts')));
const BudgetCategories = Loader(lazy(() => import('src/content/applications/Budget/Categories/Categories')));
const BudgetCurrencies = Loader(lazy(() => import('src/content/applications/Budget/Currencies/Currencies')));
const BudgetStores = Loader(lazy(() => import('src/content/applications/Budget/Stores/Stores')));
const BudgetExchanges = Loader(lazy(() => import('src/content/applications/Budget/Exchanges/Exchanges')));
const BudgetInstallments = Loader(lazy(() => import('src/content/applications/Budget/Installments/Installments')));
const BudgetLimits = Loader(lazy(() => import('src/content/applications/Budget/Limits/Limits')));

// Fitness Management  
const FitnessWorkouts = Loader(lazy(() => import('src/content/applications/Fitness/Workouts/Workouts')));
const FitnessExercises = Loader(lazy(() => import('src/content/applications/Fitness/Exercises/Exercises')));
const FitnessLists = Loader(lazy(() => import('src/content/applications/Fitness/Lists/Lists')));
const FitnessProgressions = Loader(lazy(() => import('src/content/applications/Fitness/Progressions/Progressions')));
const FitnessSets = Loader(lazy(() => import('src/content/applications/Fitness/Sets/Sets')));
const FitnessRoutines = Loader(lazy(() => import('src/content/applications/Fitness/Routines/Routines')));
const FitnessStatus = Loader(lazy(() => import('src/content/applications/Fitness/Status/Status')));
const FitnessCatalog = Loader(lazy(() => import('src/content/applications/Fitness/Catalog')));
const FitnessReports = Loader(lazy(() => import('src/content/applications/Fitness/Reports/Reports')));

// Pages
const Overview = Loader(lazy(() => import('src/content/overview')));
const Home = Loader(lazy(() => import('src/content/pages/Home')));
const Settings = Loader(lazy(() => import('src/content/pages/Settings')));
const Travelers = Loader(lazy(() => import('src/content/applications/Reservations/Travelers/Travelers')));
const Catalog = Loader(lazy(() => import('src/content/applications/Reservations/Catalog/Catalog')));
const ParcelMovements = Loader(lazy(() => import('src/content/applications/Parcels/Movements/Movements')));
const ParcelOrders = Loader(lazy(() => import('src/content/applications/Parcels/Orders/Orders')));
const ParcelSales = Loader(lazy(() => import('src/content/applications/Parcels/Sales/Sales')));
const ParcelParcels = Loader(lazy(() => import('src/content/applications/Parcels/Parcels/Parcels')));
const ParcelShipments = Loader(lazy(() => import('src/content/applications/Parcels/Shipments/Shipments')));
const ParcelRelations = Loader(lazy(() => import('src/content/applications/Parcels/Relations/Relations')));
const ParcelCatalog = Loader(lazy(() => import('src/content/applications/Parcels/Catalog/Catalog')));
const Reservations = Loader(lazy(() => import('src/content/applications/Reservations/Reservations/Reservations')));
const Trips = Loader(lazy(() => import('src/content/applications/Reservations/Trips/Trips')));
const Changes = Loader(lazy(() => import('src/content/applications/Reservations/Changes/Changes')));

// Dashboards
const Crypto = Loader(lazy(() => import('src/content/dashboards/Crypto')));
const TripReports = Loader(lazy(() => import('src/content/dashboards/TripReports')));
const ParcelReports = Loader(lazy(() => import('src/content/dashboards/ParcelReports')));
const PartsReports = Loader(lazy(() => import('src/content/dashboards/PartsReports')));
// Stocks module
const StocksDashboard = Loader(lazy(() => import('./content/dashboards/Stocks/StocksDashboard')));
const StocksReports = Loader(lazy(() => import('./content/dashboards/Stocks/StocksReports')));

// Applications
const Messenger = Loader(
  lazy(() => import('src/content/applications/Messenger'))
);
const Transactions = Loader(
  lazy(() => import('src/content/applications/Transactions'))
);
const UserProfile = Loader(
  lazy(() => import('src/content/applications/Users/profile'))
);
const CVProfile = Loader(
  lazy(() => import('src/content/applications/Profile/Page'))
);
const ProfileCatalog = Loader(
  lazy(() => import('src/content/applications/Profile/Catalog/Catalog'))
);
const ProfileCertifications = Loader(
  lazy(() => import('src/content/applications/Profile/Certifications'))
);
const ProfileAcademic = Loader(
  lazy(() => import('src/content/applications/Profile/Academic'))
);
const ProfileWork = Loader(
  lazy(() => import('src/content/applications/Profile/Work'))
);
const ProfileProjects = Loader(
  lazy(() => import('src/content/applications/Profile/Projects'))
);
const ProfileVersions = Loader(
  lazy(() => import('src/content/applications/Profile/Versions'))
);
const ProfileTags = Loader(
  lazy(() => import('src/content/applications/Profile/Tags'))
);
const ProfileSite = Loader(
  lazy(() => import('src/content/applications/Profile/Site'))
);
const UserSettings = Loader(
  lazy(() => import('src/content/applications/Users/settings'))
);
const IdentityManagement = Loader(
  lazy(() => import('src/content/applications/Users/identities'))
);
const UserManagement = Loader(
  lazy(() => import('src/content/applications/Users/management'))
);

// Stocks pages
const StocksWallet = Loader(lazy(() => import('./content/applications/Stocks/Wallet/Wallet')));
const StocksResults = Loader(lazy(() => import('./content/applications/Stocks/Results/Results')));
const StocksCatalog = Loader(lazy(() => import('./content/applications/Stocks/Catalog/Catalog')));
const StocksPosition = Loader(lazy(() => import('./content/applications/Stocks/Position/Position')));

// Components
const Buttons = Loader(
  lazy(() => import('src/content/pages/Components/Buttons'))
);
const Modals = Loader(
  lazy(() => import('src/content/pages/Components/Modals'))
);
const Accordions = Loader(
  lazy(() => import('src/content/pages/Components/Accordions'))
);
const Tabs = Loader(lazy(() => import('src/content/pages/Components/Tabs')));
const Badges = Loader(
  lazy(() => import('src/content/pages/Components/Badges'))
);
const Tooltips = Loader(
  lazy(() => import('src/content/pages/Components/Tooltips'))
);
const Avatars = Loader(
  lazy(() => import('src/content/pages/Components/Avatars'))
);
const Cards = Loader(lazy(() => import('src/content/pages/Components/Cards')));
const Forms = Loader(lazy(() => import('src/content/pages/Components/Forms')));

// Status
const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

const routes: RouteObject[] = [
  // Authentication
  {
    path: 'login',
    element: <BaseLayout />,
    children: [
      {
        path: '',
        element: <Login />
      }
    ]
  },

  // Budget Management
  {
    path: 'budget',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="dashboard" replace />
      },
      {
        path: 'dashboard',
        element: <BudgetDashboard />
      },
      {
        path: 'dashboard2',
        element: <BudgetDashboard2 />
      },
      {
        path: 'enhanced',
        element: <EnhancedBudgetDashboard />
      },
      {
        path: 'debt',
        element: <BudgetDebt />
      },
      {
        path: 'catalog',
        element: <BudgetCatalog />
      },
      {
        path: 'settings',
        element: <BudgetSettings />
      },
      {
        path: 'reports',
        element: <BudgetReports />
      },
      {
        path: 'payments',
        element: <BudgetPayments />
      },
      {
        path: 'transactions',
        element: <BudgetTransactions />
      },
      {
        path: 'accounts',
        element: <BudgetAccounts />
      },
      {
        path: 'categories',
        element: <BudgetCategories />
      },
      {
        path: 'currencies',
        element: <BudgetCurrencies />
      },
      {
        path: 'stores',
        element: <BudgetStores />
      },
      {
        path: 'exchanges',
        element: <BudgetExchanges />
      },
      {
        path: 'installments',
        element: <BudgetInstallments />
      },
      {
        path: 'limits',
        element: <BudgetLimits />
      }
    ]
  },

  // Fitness Management
  {
    path: 'fitness',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="workouts" replace />
      },
      {
        path: 'workouts',
        element: <FitnessWorkouts />
      },
      {
        path: 'exercises',
        element: <FitnessExercises />
      },
      {
        path: 'progressions',
        element: <FitnessProgressions />
      },
      {
        path: 'sets',
        element: <FitnessSets />
      },
      {
        path: 'routines',
        element: <FitnessRoutines />
      },
      {
        path: 'catalog',
        element: <FitnessCatalog />
      }
      ,
      {
        path: 'reports',
        element: <FitnessReports />
      }
    ]
  },

  // Parts Management
  {
    path: 'parts',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'assemblies',
        element: <PartsAssemblies />
      },
      {
        path: 'components',
        element: <PartsComponents />
      },
      {
        path: 'sales',
        element: <PartsSales />
      },
      {
        path: 'catalog',
        element: <PartsCatalog />
      },
      {
        path: 'reports',
        element: <PartsReports />
      }
    ]
  },
  {
    path: '',
    element: <SidebarLayout />,
    children: [
      {
        path: 'home',
        element: <Home />
      },
      {
        path: 'settings',
        element: <Settings />
      },
      {
        path: '/',
        element: <AuthLandingRedirect />
      },
      {
        path: 'overview',
        element: <Navigate to="/" replace />
      },
      {
        path: 'status',
        children: [
          {
            path: '',
            element: <Navigate to="404" replace />
          },
          {
            path: '404',
            element: <Status404 />
          },
          {
            path: '500',
            element: <Status500 />
          },
          {
            path: 'maintenance',
            element: <StatusMaintenance />
          },
          {
            path: 'coming-soon',
            element: <StatusComingSoon />
          }
        ]
      },
      {
        path: '*',
        element: <Status404 />
      }
    ]
  },
  {
    path: 'dashboards',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="crypto" replace />
      },
      {
        path: 'crypto',
        element: <ProtectedCrypto />
      },
      {
        path: 'messenger',
        element: <Messenger />
      }
    ]
  },
  {
    path: 'management',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="transactions" replace />
      },
      {
        path: 'transactions',
        element: <Transactions />
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <Navigate to="details" replace />
          },
          {
            path: 'details',
            element: <UserProfile />
          },
          {
            path: 'settings',
            element: <UserSettings />
          }
        ]
      }
    ]
  },
  {
    path: 'users',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="management" replace />
      },
      {
        path: 'management',
        element: <UserManagement />
      },
      {
        path: 'identities',
        element: (
          <ProtectedRoute>
            <IdentityManagement />
          </ProtectedRoute>
        )
      }
    ]
  },
  {
    path: '/components',
    element: <SidebarLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="buttons" replace />
      },
      {
        path: 'buttons',
        element: <Buttons />
      },
      {
        path: 'modals',
        element: <Modals />
      },
      {
        path: 'accordions',
        element: <Accordions />
      },
      {
        path: 'tabs',
        element: <Tabs />
      },
      {
        path: 'badges',
        element: <Badges />
      },
      {
        path: 'tooltips',
        element: <Tooltips />
      },
      {
        path: 'avatars',
        element: <Avatars />
      },
      {
        path: 'cards',
        element: <Cards />
      },
      {
        path: 'forms',
        element: <Forms />
      }
    ]
  },
  {
    path: 'trip',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'trips',
        element: <Trips />
      },
      {
        path: 'changes',
        element: <Changes />
      },
      {
        path: 'travelers',
        element: <Travelers />
      },
      {
        path: 'catalog',
        element: <Catalog />
      },
      {
        path: 'reservations',
        element: <Reservations />
      },
      {
        path: 'reports',
        element: <TripReports />
      }
    ]
  },

  // Parcel Management
  {
    path: 'parcel',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'movements',
        element: <ParcelMovements />
      },
      {
        path: 'orders',
        element: <ParcelOrders />
      },
      {
        path: 'sales',
        element: <ParcelSales />
      },
      {
        path: 'parcels',
        element: <ParcelParcels />
      },
      {
        path: 'shipments',
        element: <ParcelShipments />
      },
      {
        path: 'relations',
        element: <ParcelRelations />
      },
      {
        path: 'catalog',
        element: <ParcelCatalog />
      },
      {
        path: 'reports',
        element: <ParcelReports />
      }
    ]
  },
  // Stocks Management
  {
    path: 'stocks',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <Navigate to="dashboard" replace />
      },
      {
        path: 'dashboard',
        element: <StocksDashboard />
      },
      {
        path: 'wallet',
        element: <StocksWallet />
      },
      {
        path: 'position',
        element: <StocksPosition />
      },
      {
        path: 'results',
        element: <StocksResults />
      },
      {
        path: 'catalog',
        element: <StocksCatalog />
      },
      {
        path: 'reports',
        element: <StocksReports />
      }
    ]
  },
  {
    path: 'profile',
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'catalog',
        element: <ProfileCatalog />
      },
      {
        path: 'certifications',
        element: <ProfileCertifications />
      },
      {
        path: 'academic',
        element: <ProfileAcademic />
      },
      {
        path: 'work',
        element: <ProfileWork />
      },
      {
        path: 'projects',
        element: <ProfileProjects />
      },
      {
        path: 'versions',
        element: <ProfileVersions />
      },
      {
        path: 'tags',
        element: <ProfileTags />
      },
      {
        path: 'page',
        element: <CVProfile />
      },
      {
        path: 'cv',
        element: <CVProfile />
      }
    ]
  },
  {
    path: 'profile/site',
    element: <BaseLayout />,
    children: [
      { path: '', element: <ProfileSite /> },
      { path: '*', element: <ProfileSite /> }
    ]
  }
];

export default routes;
